import React, { useState, useEffect, useCallback } from "react";
import axios from '../../api/apiClient';

import {
  CreateTableNew as CreateTable,
  SearchBar,
  Button,
  GeneralMessage,
  useGeneralMessage
} from '../../common/APICommon';
import { SetAuditTrail, SetAuditTrailBulk } from './../Reports/AuditTrailReportEmployeeSetup';
import {
  Input,
} from '../../common/Input';
import { ConfirmationModal } from '../../common/Modal';
import { token, domain } from '../Security';
import { LoadingSpinner } from '../../common/Loading';
import { fileToBase64, base64ToImageJpg } from "../../utils/fileUtils";
// icon
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteForever';
import VisibilityIcon from '@mui/icons-material/Visibility';

function Company({ user, isHideSideMenu }) {
  // Use the hook for message management
  const { showMessage, message, isSuccess, showGeneralMessage } = useGeneralMessage();

  var headers = [
    "ID",
    "Name",
    "Address",
    "Tin #",
    "Logo",
    "Status",
    "Actions"
  ]
  
  var accessors = [
    "id",
    "name",
    "address",
    "tin",
    "logo",
    "isActive",
    "actions"
  ]

  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingRequest, setLoadingRequest] = useState(false);
  const [data, setData] = useState([]);
  const [selectedData, setSelectedData] = useState(null);

  // Form state
  const [form, setForm] = useState({
    name: "",
    address: "",
    tin: "",
    logo: "",
    isActive: true,
    logoFile: null
  });

  const resetLocalVariables = () => {
    setForm({
      name: "",
      address: "",
      tin: "",
      logo: "",
      isActive: true,
      logoFile: null
    });
    setSelectedData(null);
  }

  // Retrieve
  const [filterData, setFilterData] = useState([]);
  // Add
  const [showAddForm, setShowAddForm] = useState(false);
  // Update
  const [showEditForm, setShowEditForm] = useState(false);
  // Delete
  const [showDeleteForm, setShowDeleteForm] = useState(false);
  // View Logo
  const [showViewLogo, setShowViewLogo] = useState(false);
  const [viewLogoData, setViewLogoData] = useState("");

  const apiUrl = domain + "/api/Company";

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      setData(response.data);
      setFilterData(response.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilter = (value) => {
    setCurrentPage(1);
    const lowercaseValue = value.toLowerCase();
    
    if (!value) {
      // If input is empty, reset to original list
      setData(filterData);
      return;
    }

    const res = filterData.filter((f) => {
      const match1 = f.name?.toString().toLowerCase().includes(lowercaseValue);
      const match2 = f.id?.toString().startsWith(lowercaseValue);
      const match3 = f.address?.toString().toLowerCase().includes(lowercaseValue);
      const match4 = f.tin?.toString().toLowerCase().includes(lowercaseValue);
      return match1 || match2 || match3 || match4;
    });
    
    setData(res);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    let base64String = "";
    let photoToBeSave = "";
    
    if (form.logoFile) {
      try {
        base64String = await fileToBase64(form.logoFile);
        photoToBeSave = base64String.split(',')[1];
      } catch (error) {
        console.error('Error converting file to base64:', error);
        showGeneralMessage("Error processing logo file", false);
        return;
      }
    }

    // Prepare data for API
    const apiData = {
      name: form.name || "",
      address: form.address || "",
      tin: form.tin || "",
      logo: form.logoFile ? photoToBeSave : null,
      isActive: form.isActive !== undefined ? form.isActive : true,
    };

    console.log('Add Submitting data:', apiData);

    try {
      setLoadingRequest(true);
      await axios.post(apiUrl, apiData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Audit trail logic
      await SetAuditTrail({
        setupArea: "Company", 
        actionTaken: "insert", 
        field: "Add", 
        newValue: `${apiData.name}`, 
        oldValue: "-", 
        dateSaved: new Date(), 
        savedById: user.id
      });
      
      await fetchData();

      showGeneralMessage("Company created successfully!", true);
      closeAddHandleClick();
    } catch (error) {
      console.error('Error creating data:', error.response || error);

      const errorMessage = error.response?.data?.message ||
        error.response?.data?.errors?.[0]?.message ||
        "Failed to create company";
      showGeneralMessage(errorMessage, false);
    } finally {
      setLoadingRequest(false);
    }
  };

  const handleUpdate = async (event) => {
    event.preventDefault();

    if (!selectedData?.id) {
      showGeneralMessage("No record selected for update", false);
      return;
    }

    let base64String = "";
    let photoToBeSave = "";
    
    if (form.logoFile) {
      try {
        base64String = await fileToBase64(form.logoFile);
        photoToBeSave = base64String.split(',')[1];
      } catch (error) {
        console.error('Error converting file to base64:', error);
        showGeneralMessage("Error processing logo file", false);
        return;
      }
    }

    const apiData = {
      name: form.name || "",
      address: form.address || "",
      tin: form.tin || "",
      logo: form.logoFile ? photoToBeSave : (selectedData.logo || null),
      isActive: form.isActive !== undefined ? form.isActive : true,
    };

    try {
      setLoadingRequest(true);
      await axios.put(`${apiUrl}/${selectedData.id}`, apiData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Audit trail logic
      let auditInfo = [];

      if (apiData.name !== selectedData.name) {
        auditInfo.push({
          setupArea: "Company",
          actionTaken: "update",
          field: "Name",
          newValue: `${apiData.name}`,
          oldValue: `${selectedData.name}`,
          dateSaved: new Date(),
          savedById: user.id,
        });
      }

      if (apiData.address !== selectedData.address) {
        auditInfo.push({
          setupArea: "Company",
          actionTaken: "update",
          field: "Address",
          newValue: `${apiData.address}`,
          oldValue: `${selectedData.address}`,
          dateSaved: new Date(),
          savedById: user.id,
        });
      }

      if (apiData.tin !== selectedData.tin) {
        auditInfo.push({
          setupArea: "Company",
          actionTaken: "update",
          field: "Tin",
          newValue: `${apiData.tin}`,
          oldValue: `${selectedData.tin}`,
          dateSaved: new Date(),
          savedById: user.id,
        });
      }

      // Check if logo was changed
      const hasLogoChanged = form.logoFile || (!form.logoFile && !selectedData.logo && apiData.logo !== null);
      if (hasLogoChanged) {
        auditInfo.push({
          setupArea: "Company",
          actionTaken: "update",
          field: "Logo",
          newValue: apiData.logo ? "Updated Logo" : "Removed Logo",
          oldValue: selectedData.logo ? "Had Logo" : "No Logo",
          dateSaved: new Date(),
          savedById: user.id,
        });
      }

      if (apiData.isActive !== selectedData.isActive) {
        auditInfo.push({
          setupArea: "Company",
          actionTaken: "update",
          field: "Is Active?",
          newValue: `${apiData.isActive ? "Active" : "Inactive"}`,
          oldValue: `${selectedData.isActive ? "Active" : "Inactive"}`,
          dateSaved: new Date(),
          savedById: user.id,
        });
      }

      if (auditInfo.length > 0) {
        await SetAuditTrailBulk({ data: auditInfo });
      }

      setShowEditForm(false);
      await fetchData();

      showGeneralMessage("Company updated successfully!", true);
      closeEditHandleClick();
    } catch (error) {
      console.error('Error updating data:', error.response || error);

      const errorMessage = error.response?.data?.message ||
        error.response?.data?.errors?.[0]?.message ||
        "Failed to update company";
      showGeneralMessage(errorMessage, false);
    } finally {
      setLoadingRequest(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedData?.id) {
      showGeneralMessage("No record selected for deletion", false);
      return;
    }

    try {
      setLoadingRequest(true);
      await axios.delete(`${apiUrl}/${selectedData.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Audit trail logic
      await SetAuditTrail({
        setupArea: "Company", 
        actionTaken: "delete", 
        field: "Delete", 
        newValue: `${selectedData.name}`, 
        oldValue: "-", 
        dateSaved: new Date(), 
        savedById: user.id
      });
      
      await fetchData();

      showGeneralMessage("Company deleted successfully!", true);
      closeDeleteHandleClick();
    } catch (error) {
      console.error('Error deleting data:', error.response || error);

      const errorMessage = error.response?.data?.message ||
        error.response?.data?.errors?.[0]?.message ||
        "Failed to delete company";
      showGeneralMessage(errorMessage, false);
    } finally {
      setLoadingRequest(false);
    }
  };

  const editHandleClick = (data) => {
    setSelectedData(data);

    setForm({
      name: data.name || "",
      address: data.address || "",
      tin: data.tin || "",
      logo: data.logo || "",
      isActive: data.isActive !== undefined ? data.isActive : true,
      logoFile: null
    });
    openEditHandleClick();
  };

  const deleteHandleClick = (data) => {
    setSelectedData(data);
    setShowDeleteForm(true);
  };

  const viewLogoHandleClick = (data) => {
    setViewLogoData(data.logo);
    setShowViewLogo(true);
  };

  const openAddHandleClick = () => {
    setShowAddForm(true);
  };

  const closeAddHandleClick = () => {
    setShowAddForm(false);
    resetLocalVariables();
  };

  const openEditHandleClick = () => {
    setShowEditForm(true);
  };

  const closeEditHandleClick = () => {
    setShowEditForm(false);
    resetLocalVariables();
  };

  const openDeleteHandleClick = () => {
    setShowDeleteForm(true);
  };

  const closeDeleteHandleClick = () => {
    setShowDeleteForm(false);
    setSelectedData(null);
  };

  const closeViewLogo = () => {
    setShowViewLogo(false);
    setViewLogoData("");
  };

  const actions = [
    {
      name: "View",
      tooltip: "view logo",
      icon: <VisibilityIcon fontSize="small" />,
      className: "bg-sky-600 hover:bg-sky-700 flex items-center",
      function: viewLogoHandleClick,
      visible: true,
    },
    {
      name: "Edit",
      tooltip: "edit",
      icon: <EditIcon fontSize="small" />,
      className: "bg-green-500 hover:bg-green-600 flex items-center",
      function: editHandleClick,
      visible: true,
    },
    {
      name: "Delete",
      tooltip: "delete",
      icon: <DeleteIcon fontSize="small" />,
      className: "bg-red-500 hover:bg-red-600 flex items-center",
      function: deleteHandleClick,
      visible: true,
    },
  ];

  return (
    <div>
      <div className={`MainBody row ${isHideSideMenu ? "MainBody2" : ""}`}>
        <div className="LeftSpace"></div>
        <div className={`BodySpace ${isHideSideMenu ? "BodySpace2" : ""}`}>
          <div className="MainteInfo shadow-xl">
            <div className="mainteHeader">
              <div className='sub-title row'>
                <span style={{ padding: "0 5px 0 0", color: "#b0b0b0" }}>{"Maintenance >"} </span>
                <span style={{ borderBottom: "1px solid black" }}>{"Company"}</span>
              </div>
              <h3>Company</h3>
            </div>
            <div className='space-y-4 p-4'>
              <SearchBar handleFilter={handleFilter} />

              <Button icon={<AddIcon />}
                name={"Add Company"}
                className={"bg-customGreen hover:bg-customGreenHover border-none cursor-pointer"}
                onClick={openAddHandleClick}
              />
              <CreateTable
                tableName={"Company"}
                headers={headers}
                accessors={accessors}
                data={data}
                actions={actions}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                itemsPerPage={10}
                totalItems={data?.length}
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Add Form */}
      <AddEditForm
        isOpen={showAddForm}
        onClose={closeAddHandleClick}
        form={form}
        setForm={setForm}
        handleSubmit={handleSubmit}
        modalName={"Add Company"}
        mode="add"
        showGeneralMessage={showGeneralMessage}
      />
      
      {/* Edit Form */}
      <AddEditForm
        isOpen={showEditForm}
        onClose={closeEditHandleClick}
        form={form}
        setForm={setForm}
        handleSubmit={handleUpdate}
        modalName={"Update Company"}
        mode="edit"
        showGeneralMessage={showGeneralMessage}
      />
      
      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        visible={showDeleteForm}
        onClose={closeDeleteHandleClick}
        onConfirm={handleDelete}
        title="Delete Company"
        message="Are you sure you want to delete this company?"
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        isLoading={loadingRequest}
      />

      {/* View Logo Modal */}
      <ViewLogoModal
        isOpen={showViewLogo}
        onClose={closeViewLogo}
        logoData={viewLogoData}
      />

      {/* Use the reusable GeneralMessage component */}
      <GeneralMessage
        form={showMessage}
        message={message}
        isSuccess={isSuccess}
      />

      <LoadingSpinner loading={loadingRequest || loading} />

    </div>
  )
}

function AddEditForm({
  isOpen,
  modalName,
  onClose,
  form,
  setForm,
  handleSubmit,
  mode = "add", // "add", "edit"
  showGeneralMessage
}) {
  const [errorState, setErrorState] = useState({
    name: false,
    address: false,
    tin: false
  });

  const submit = (e) => {
    e.preventDefault();
    
    // Validation
    const newErrorState = {
      name: !form.name,
      address: !form.address,
      tin: !form.tin
    };
    
    if (newErrorState.name || newErrorState.address || newErrorState.tin) {
      setErrorState(newErrorState);
      return;
    }
    
    handleSubmit(e);
    setErrorState({ name: false, address: false, tin: false });
  };

  const handleClose = () => {
    onClose();
    setErrorState({ name: false, address: false, tin: false });
  };

  const handleInputChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith("image/")) {
      setForm(prev => ({
        ...prev,
        logoFile: file
      }));
    } else {
      showGeneralMessage("Please select an image file.", false);
    }
  };

  const removeLogo = () => {
    setForm(prev => ({
      ...prev,
      logoFile: null,
      logo: ""
    }));
  };

  if (!isOpen) return null;

  const isEditMode = mode === "edit";
  const hasLogo = form.logoFile || form.logo;
  const logoPreview = form.logoFile 
    ? URL.createObjectURL(form.logoFile) 
    : (form.logo ? `data:image/jpeg;base64,${form.logo}` : null);

  return (
    <>
      <div
        role="dialog"
        aria-modal="true"
        className="fixed inset-0 z-[10000] flex items-center justify-center overflow-auto bg-black/50 p-3 text-gray-700"
      >
        <div className="relative flex h-auto max-h-[90vh] w-full flex-col rounded-lg bg-gray-50 shadow-xl md:w-[90%] lg:w-[50%] xl:w-[50%]">

          {/* Header */}
          <div className="sticky top-0 z-10 flex items-center justify-between rounded-t-lg border-b border-gray-200 bg-white px-6 py-2 shadow-sm">
            <h2 className="flex items-center space-x-2 text-xl font-semibold text-gray-800">
              <span className="text-xl text-gray-600">{modalName}</span>
            </h2>

            <button
              onClick={handleClose}
              className="border-none bg-transparent p-1 text-gray-400 transition-colors hover:text-gray-700"
              aria-label="Close modal"
            >
              <CloseIcon fontSize="medium" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-grow space-y-4 overflow-y-auto p-6">

            {/* Company Form */}
            <div className="rounded-lg border border-gray-300 bg-white px-4 shadow-sm">
              <div className="border-b border-gray-300 py-1">
                <h3 className="font-medium text-gray-700">Company Information</h3>
              </div>
              <div className="grid grid-cols-1 gap-4 pb-3 pt-3">
                <Input
                  label="Name:"
                  value={form.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Enter company name"
                  isRequired={true}
                  error={errorState.name && !form.name}
                  errorMessage="Please fill up this field"
                />
                
                <Input
                  label="Address:"
                  value={form.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  placeholder="Enter company address"
                  isRequired={true}
                  error={errorState.address && !form.address}
                  errorMessage="Please fill up this field"
                />
                
                <Input
                  label="Tin #:"
                  value={form.tin}
                  onChange={(e) => handleInputChange('tin', e.target.value)}
                  placeholder="Enter TIN number"
                  isRequired={true}
                  error={errorState.tin && !form.tin}
                  errorMessage="Please fill up this field"
                />
                
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-600">
                    Logo:
                  </label>
                  <div className="flex flex-col items-center space-y-3">
                    {logoPreview ? (
                      <>
                        <img 
                          src={logoPreview} 
                          alt="Company Logo Preview" 
                          className="h-40 w-auto rounded border border-gray-300 object-contain"
                        />
                        <button
                          type="button"
                          onClick={removeLogo}
                          className="cursor-pointer rounded border-none bg-red-500 px-4 py-2 text-sm text-white hover:bg-red-600"
                        >
                          Remove
                        </button>
                      </>
                    ) : (
                      <div className="flex h-40 w-40 items-center justify-center rounded border-2 border-dashed border-gray-300 bg-gray-50">
                        <span className="text-sm text-gray-500">No logo selected</span>
                      </div>
                    )}
                    
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        className="hidden"
                        id="logo-upload"
                      />
                      <div className="cursor-pointer rounded bg-blue-500 px-4 py-2 text-sm text-white hover:bg-blue-600">
                        {hasLogo ? "Change Logo" : "Upload Logo"}
                      </div>
                    </label>
                    <span className="text-xs text-gray-500">Supported formats: JPG, PNG</span>
                  </div>
                </div>
                
                <div className="flex items-center pt-2">
                  <input
                    type="checkbox"
                    checked={form.isActive !== undefined ? form.isActive : true}
                    onChange={(e) => handleInputChange('isActive', e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label className="ml-2 text-sm font-medium text-gray-600">
                    Active
                  </label>
                </div>
              </div>
            </div>

          </div>

          {/* Footer */}
          <div className="flex justify-end space-x-2 rounded-b-lg border-t border-gray-200 bg-white p-5 shadow-sm">
            <button
              onClick={handleClose}
              className="cursor-pointer rounded border-none bg-gray-400 px-4 py-2 text-white transition-colors hover:bg-gray-500"
            >
              Cancel
            </button>

            <button
              onClick={submit}
              className="cursor-pointer rounded border-none bg-blue-500 px-4 py-2 text-white transition-colors hover:bg-blue-600"
            >
              {isEditMode ? "Update" : "Save"}
            </button>
          </div>

        </div>
      </div>
    </>
  );
}

function ViewLogoModal({ isOpen, onClose, logoData }) {
  if (!isOpen) return null;

  const logoUrl = logoData ? `data:image/jpeg;base64,${logoData}` : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-3"
    >
      <div className="relative flex w-full max-w-2xl flex-col rounded-lg bg-white shadow-xl">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between rounded-t-lg border-b border-gray-200 bg-white px-6 py-2 shadow-sm"
            style ={{ borderBottom: '#dee0e4ff 1px solid'}}>
          <h2 className="text-xl font-semibold text-gray-800">Company Logo</h2>
          <button
              onClick={onClose}
              className="border-none bg-transparent p-1 text-gray-400 transition-colors hover:text-gray-700"
              aria-label="Close modal"
            >
              <CloseIcon fontSize="medium" />
            </button>
        </div>

        {/* Body */}
        <div className="flex min-h-[300px] items-center justify-center p-6">
          {logoUrl ? (
            <img 
              src={logoUrl} 
              alt="Company Logo" 
              className="max-h-[400px] max-w-full object-contain"
            />
          ) : (
            <div className="text-center text-gray-500">
              <div className="mb-4 text-5xl">🏢</div>
              <p className="text-lg">No logo available</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="shadow-t-sm flex justify-end space-x-2 rounded-b-lg border-t border-gray-200 bg-white p-5"
            style ={{ borderTop: '#dee0e4ff 1px solid'}}>
          <button
            onClick={onClose}
            className="rounded border-none bg-gray-500 px-4 py-2 text-white hover:bg-gray-600"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default Company